/* eslint-disable max-lines */
import uuid from 'uuidv4';
import _isEmpty from 'lodash/isEmpty';
import _toLower from 'lodash/toLower';
import { saveConfiguration } from '@gcc/configurator';
import {
  CREATE_DESIGN,
  CREATE_DESIGN_FAILED,
  UPDATE_ITEM_PRODUCT,
  UPDATE_ITEM_PRODUCT_FAILED,
  GET_DESIGN_BY_ITEM,
  GET_DESIGN_BY_ITEM_FAILED,
  SAVE_CONFIG_START,
  SAVE_CONFIG_SUCCESS,
  SAVE_CONFIG_FAILED,
  SAVE_ONLINE_DESIGN_SUCCESS,
  SAVE_ONLINE_DESIGN_FAILED,
  SEND_DESIGN_EMAIL_SUCCESS,
  SEND_DESIGN_EMAIL_FAILED,
  VALIDATE_SELECTED_CHOICES,
} from './actionTypes';
import { setStore } from './store';
import { getProductDetails } from './Products';
import { getFeatureGates } from '../utils/featureGates';
import thdCustomer from '../utils/thdCustomer';
import { getConfig } from '../config';
import { trackSaveContinueDesign, trackUpdateDesign } from '../utils/analytics';
import saveOnlineDesign from '../api/saveOnlineDesign';
import { storeInfo as getStoreInfo } from '../api/storeInfoApi';
import { showMinimumOrderModal } from '../Events';
import log from '../api/logApi';
import Api from '../api/axios';
import { Utils } from '../utils/urlUtils';
import toCamelCase from '../utils/camelCase';

const getCountertopsBaseUrl = () => `${getConfig()?.customBaseUrl}/countertops/api`;
const getCountertopsCartBaseUrl = () => `${getConfig()?.customBaseUrl}/countertops/estimate`;

const formatHours = (num) => {
  if (!num || Number.isNaN(num)) {
    return '';
  }
  const rem = num % 12;
  const formattedHours = rem === 0 ? num : rem;
  return `${formattedHours}${num > 11 ? 'pm' : 'am'}`;
};

const getformattedHours = (hours) => {
  if (!hours) {
    return '';
  }
  const { open, close } = hours;
  const openHours = open ? open.substring(0, open.indexOf(':')) : '';
  const closeHours = close ? close.substring(0, close.indexOf(':')) : '';

  return `${formatHours(openHours)} - ${formatHours(closeHours)}`;
};

const
  formatStoreAddress = (address) => {
    if (!address) {
      return '';
    }
    const {
      street = '', city = '', state = '', postalCode = '',
    } = address;
    return `${street}<br /> ${city}, ${state} ${postalCode}`;
  };

const formatStoreHours = (storeHours) => {
  if (!storeHours) {
    return '';
  }
  const { monday = '', sunday = '' } = storeHours;
  const mondayToSaturday = `M-Sat. ${getformattedHours(monday)}`;
  const sundayHours = `Sun. ${getformattedHours(sunday)}`;

  return `${mondayToSaturday} <br /> ${sundayHours}`;
};

// get environment base redirect URLs
const getEnvBaseRedirect = () => {
  const env = getConfig()?.env;
  return Utils.containsNucleusPath()
    ? `${env.protocol}//${env.hostname}/render-nucleus`
    : `${env.protocol}//${env.hostname}:${env.port}/custom-countertops`;
};

const redirectToCart = (designId) => {
  const envBaseRedirect = getEnvBaseRedirect();
  /* eslint-disable max-len */
  const redirectUrl = `${getCountertopsCartBaseUrl()}/${designId}?saved=1&envBaseRedirect=${envBaseRedirect}`;
  if (typeof window !== 'undefined') {
    window.location.href = redirectUrl;
  }
};

const redirectToLogin = (omsId, itemId) => {
  const env = getConfig()?.env;
  const returnUrl = `/custom-countertops/countertops/configurator/${omsId}/item/${itemId}${env.search === 'undefined' ? '/' : env.search}`;
  if (typeof window !== 'undefined') {
    window.location.href = `${getConfig()?.thdLoginUrl}?redirect=${returnUrl}&save=1&ref=${window.location.hostname}`;
  }
};

export const addDesign = (
  name,
  productLine,
  zip,
  containerType,
  defaultContainerName,
  itemLabelPrefix,
  itemType,
  storeNumber,
  doSaveConfiguration = false,
) => (dispatch) => {
  const designId = uuid();
  const containerId = uuid();
  const itemId = uuid();
  const groupId = uuid();

  const url = `${getCountertopsBaseUrl()}/designs/save`;
  const envBaseRedirect = getEnvBaseRedirect();
  const pirateCartUrl = `${getCountertopsCartBaseUrl()}/${designId}?envBaseRedirect=${envBaseRedirect}`;
  const designAttributes = [
    { designId, name: 'Origin', value: 'Online' },
    { designId, name: 'PirateCartUrl', value: pirateCartUrl }
  ];

  const newDesign = {
    designId,
    name,
    productLine,
    zipCode: zip,
    storeNumber,
    attributes: designAttributes
  };
  const newContainer = {
    containerId,
    name: defaultContainerName || `${containerType} 1`,
    containerType,
  };
  const newItem = {
    designId,
    itemId,
    label: `${itemLabelPrefix} 1`,
    itemSource: 'Autobahn',
    containerId,
    attributes: [],
    groupId,
  };
  const payload = {
    design: newDesign,
    containers: [newContainer],
    items: [newItem],
  };

  Api().post(url, payload)
    .then((response) => {
      dispatch({
        type: CREATE_DESIGN,
        payload: response.data,
      });
      if (doSaveConfiguration) {
        dispatch(saveConfiguration({ clearOnSave: false }));
      }
    }).catch((err) => {
      dispatch({
        type: CREATE_DESIGN_FAILED,
        payload: err.response && err.response.data,
      });
    });
};

export const saveConfig = () => (dispatch, getState) => {
  const {
    design: { currentDesign },
    store: { storeNumber },
    configurator: {
      pricing: {
        pricing: {
          strikeThroughPrice: {
            price,
          },
        },
      },
      productInfo: { minimumRetailAmount },
    },
    draftingTool: { designClassification },
  } = getState();

  if (price < minimumRetailAmount) {
    showMinimumOrderModal();
    return;
  }

  dispatch({ type: VALIDATE_SELECTED_CHOICES, payload: { designClassification } });

  dispatch({
    type: SAVE_CONFIG_START,
  });
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }

  if (_isEmpty(currentDesign)) {
    dispatch(addDesign('Design 1', 8, '', 'Room', 'Room 1', 'Countertop', 'Countertop', storeNumber, true));
  } else {
    dispatch(saveConfiguration({ clearOnSave: false }));
  }
};

const sendEmail = (designId, itemId, svocId) => (dispatch, getState) => {
  const url = `${getCountertopsBaseUrl()}/email`;
  const envBaseRedirect = getEnvBaseRedirect();
  const state = getState();
  const { products: { currentOmsId }, store: { storeInfo = {} } } = state;
  const {
    storeId = '', name = '', address = null, storeHours = null, phone: storePhoneNumber = '',
  } = storeInfo;
  const { email: recipientEmailId } = thdCustomer;
  return Api().post(url, {
    storeNumber: storeId,
    productId: currentOmsId,
    designId,
    itemId,
    recipientEmailId,
    storeName: name,
    storeHours: formatStoreHours(storeHours),
    storeAddress: formatStoreAddress(address),
    storePhoneNumber,
    envBaseRedirect,
    envQueryString: '/',
    svocId
  }).catch((ee) => {
    log(`Error sending online save design email: ${ee}, storeId=${storeId} designGuid: ${designId}, itemId: ${itemId}`,
      'Error');
    throw ee;
  });
};

export const associateDesign = () => (dispatch, getState) => {
  const {
    design: {
      currentDesign: { name },
      currentItem: { itemId, designId },
      designAssociated,
      emailSent,
    },
  } = getState();
  if (thdCustomer.isLoggedIn) {
    const { svocID: svocId } = thdCustomer;
    if (!designAssociated) {
      return saveOnlineDesign(svocId, designId, name).then(() => {
        dispatch({
          type: SAVE_ONLINE_DESIGN_SUCCESS,
        });
        if (!emailSent) {
          return dispatch(sendEmail(designId, itemId, svocId)).then(() => {
            dispatch({
              type: SEND_DESIGN_EMAIL_SUCCESS,
            });
          }).catch((ee) => {
            dispatch({
              type: SEND_DESIGN_EMAIL_FAILED,
            });
            throw ee;
          });
        }
        return Promise.resolve();
      }).catch((ee) => {
        dispatch({
          type: SAVE_ONLINE_DESIGN_FAILED,
        });
        throw ee;
      });
    }
    if (!emailSent) {
      return dispatch(sendEmail(designId, itemId)).then(() => {
        dispatch({
          type: SEND_DESIGN_EMAIL_SUCCESS,
        });
      }).catch((ee) => {
        dispatch({
          type: SEND_DESIGN_EMAIL_FAILED,
        });
        throw ee;
      });
    }
  }
  return Promise.resolve();
};

export const addItemProduct = (designId, itemId, configurationGuid) => (dispatch, getState) => {
  dispatch({
    type: SAVE_CONFIG_START,
  });

  const url = `${getCountertopsBaseUrl()}/designs/${designId}/items/${itemId}`;
  const state = getState();
  const { design: { configurationSaved, designAssociated, emailSent } } = state;
  const inEditFlow = configurationSaved;
  return Api().post(url, { configurationGuid })
    .then((response) => response.data)
    .then((updatedDesign) => {
      dispatch({
        type: UPDATE_ITEM_PRODUCT,
        payload: updatedDesign,
      });
      // track analytics
      if (inEditFlow) { trackUpdateDesign(); } else { trackSaveContinueDesign(); }
    })
    .then(() => {
      const enableSaveAndContinue = getFeatureGates()?.enableSaveAndContinue;
      if (!designAssociated || !emailSent) {
        if (thdCustomer.isLoggedIn) {
          return dispatch(associateDesign()).then(() => enableSaveAndContinue);
        }

        const { products: { currentOmsId } } = state;
        redirectToLogin(currentOmsId, itemId);
        return Promise.resolve(false);
      }

      if (inEditFlow && thdCustomer.isLoggedIn) {
        const { svocID: svocId } = thdCustomer;
        return dispatch(sendEmail(designId, itemId, svocId))
          .then(() => enableSaveAndContinue);
      }

      return Promise.resolve(enableSaveAndContinue);
    })
    .then((shouldRedirectToCart) => {
      if (shouldRedirectToCart) {
        redirectToCart(designId);
      }
    })
    .then(() => {
      dispatch({
        type: SAVE_CONFIG_SUCCESS,
      });
    })
    .catch((err) => {
      dispatch({
        type: SAVE_CONFIG_FAILED,
      });
      dispatch({
        type: UPDATE_ITEM_PRODUCT_FAILED,
        payload: err.response && err.response.data,
      });
      throw err;
    });
};

export const saveFailed = () => (dispatch) => {
  dispatch({
    type: SAVE_CONFIG_FAILED,
  });
};

const setStoreNumber = (item, dispatch, isPostFormLaminate) => {
  if (item && item.itemProduct) {
    const { choices, skuNumber } = item.itemProduct;
    // hacky but no other option..
    const storeIdChoice = choices.find((cc) => cc.optionName === 'Store Id');
    const surfaceSkuChoice = choices.find((cc) => cc.optionName === 'Surface Code');
    if (storeIdChoice && surfaceSkuChoice) {
      // overwrite the store number if its part of test stores
      const storeNumber = storeIdChoice.choiceValue;
      const configSkuNumber = surfaceSkuChoice.choiceValue;
      if (storeNumber && configSkuNumber) {
        dispatch(getProductDetails({ omsId: skuNumber, storeNumber, configSkuNumber, isPostFormLaminate }));
        return getStoreInfo(+storeNumber);
      }
    }
  }
  return Promise.resolve();
};

export const getDesignByItem = (itemId, isFromSaveLogin = false, isFromSavingLogin = false, isPostFormLaminate) => (dispatch) => {
  const url = `${getCountertopsBaseUrl()}/designs/item/${itemId}`;

  return Api().get(url)
    .then((response) => response.data)
    .then((response) => {
      // Ensure all keys are camelCase
      const camelCaseResponse = toCamelCase(response);
      const { designInfo: { design, items } = {}, status } = camelCaseResponse;
      const currentItem = (items && items.find((i) => _toLower(i.itemId) === _toLower(itemId))) || {};

      // set storeid based on item config choices
      setStoreNumber(currentItem, dispatch, isPostFormLaminate)
        .then((data) => {
          if (data?.stores && data?.stores?.length) {
            dispatch(setStore(data.stores[0]));
          }
          dispatch({
            type: GET_DESIGN_BY_ITEM,
            payload: {
              currentDesign: design,
              currentItem,
              status,
              configurationSaved: true,
              designAssociated: !isFromSaveLogin && !isFromSavingLogin,
              emailSent: !isFromSaveLogin && !isFromSavingLogin,
            },
          });
        });

      return design;
    })
    .catch((err) => {
      dispatch({
        type: GET_DESIGN_BY_ITEM_FAILED,
        payload: err.response && err.response.data,
      });
    });
};
